// Copied from platform-app components/pos/receipt/ReceiptPreview.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
// POS Defaults: super-admin-ui has neither `qrcode` nor `jsbarcode` (and nothing may
// be installed), so the QR uses `qrcode.react` (already a dependency) and the order
// barcode uses super-admin-ui's own Code 128 encoder (`components/Barcode`).
import { QRCodeSVG } from 'qrcode.react';
import Barcode from '../../../../Barcode';
import type { PrinterReceiptConfig, ReceiptBlock, BusinessDetailLine } from '@platform/shared-types';
import { DEFAULT_BUSINESS_DETAIL_STYLES } from './blockDefaults';
import ShimmerImage from '../../ShimmerImage';

// --- Business detail line renderer (locked section, with optional overrides) ---
function BusinessLine({ line, defaultContent, styleKey }: {
  line: BusinessDetailLine | null | undefined;
  defaultContent: string | undefined;
  styleKey: keyof typeof DEFAULT_BUSINESS_DETAIL_STYLES;
}) {
  const def = DEFAULT_BUSINESS_DETAIL_STYLES[styleKey];
  const o = line || {};
  const content = (o.content && o.content.trim()) || defaultContent;
  if (!content) return null;
  const size = o.size ?? def.size;
  const align = o.align ?? def.align;
  const bold = o.bold ?? def.bold;
  const italic = o.italic ?? def.italic;
  const underline = o.underline ?? def.underline;

  const sizeCls = ({ S: 'text-[10px]', N: 'text-[12px]', L: 'text-[14px]', XL: 'text-[16px]' }[size]);
  const alignCls = ({ L: 'text-left', C: 'text-center', R: 'text-right' }[align]);
  const cls = [sizeCls, alignCls, bold && 'font-bold', italic && 'italic', underline && 'underline'].filter(Boolean).join(' ');
  return <div className={cls}>{content}</div>;
}

// --- Order barcode (Code 128, locked default-on) ---
function OrderBarcode({ value }: { value: string }) {
  return (
    <div className="text-center my-2" role="img" aria-label={`Barcode ${value}`}>
      <Barcode value={value} height={38} moduleWidth={2} color="#000" className="inline-block" />
      <div style={{ fontSize: 11 }}>{value}</div>
    </div>
  );
}

function QrImg({ data, size, align, caption, spacingAbove }: {
  data: string;
  size: 'S' | 'M' | 'L';
  align: 'L' | 'C' | 'R';
  caption: string | null;
  spacingAbove: 'none' | 'S' | 'L';
}) {
  const dim = ({ S: 60, M: 90, L: 120 }[size]);
  const alignCls = ({ L: 'text-left', C: 'text-center', R: 'text-right' }[align]);
  const spaceCls = ({ none: '', S: 'mt-1', L: 'mt-3' }[spacingAbove]);
  return (
    <div className={`${alignCls} ${spaceCls}`}>
      {data
        ? <QRCodeSVG value={data} size={dim} level="M" marginSize={1} fgColor="#000" bgColor="#fff" data-testid="qr" style={{ width: dim, height: dim, display: 'inline-block' }} />
        : <div data-testid="qr" style={{ width: dim, height: dim, background: 'repeating-linear-gradient(45deg,#111 0 2px,transparent 2px 4px)', border: '4px solid #111', margin: '0 auto' }} />}
      {caption && <div className="text-[10px] text-center mt-1">{caption}</div>}
    </div>
  );
}

export interface ReceiptOrder {
  legalName: string;
  address: string;
  taxId?: string;
  phone?: string;
  orderNumber: string;
  timestamp: number;
  cashier?: string;
  items: {
    qty: number;
    name: string;
    price: number;
    modifiers?: string[];
    /** Per-line discount attribution lines (Promo / Manual / Reward). When
     *  populated, the preview prints one indented line per entry directly
     *  under the item — mirrors the thermal receipt's behaviour. */
    discountSummary?: { source: 'manual' | 'loyalty' | 'promotion' | 'cash' | 'employee'; sourceName: string; amount: number }[];
  }[];
  subtotal: number;
  /** Total discount applied (manual + loyalty + promotion). Rendered as a
   *  negative "Discount: -X.XX" line directly under Subtotal. Optional —
   *  receipts with no discount omit the line entirely. */
  discount?: number;
  tax: number;
  fees?: { label: string; amount: number }[];
  surcharges?: { label: string; amount: number }[];
  tip?: number;
  total: number;
  payment: string;
  change?: number;
}

interface Props {
  config: PrinterReceiptConfig;
  order: ReceiptOrder;
  width: '80mm' | '58mm';
}

const sizeClass = (s: 'S' | 'N' | 'L' | 'XL') =>
  ({ S: 'text-[10px]', N: 'text-[12px]', L: 'text-[14px]', XL: 'text-[16px]' }[s]);

const alignClass = (a: 'L' | 'C' | 'R') =>
  ({ L: 'text-left', C: 'text-center', R: 'text-right' }[a]);

const spacingClass = (s: 'none' | 'S' | 'L') =>
  ({ none: '', S: 'mt-1', L: 'mt-3' }[s]);

const dividerClass = (s: 'dashed' | 'solid' | 'double') =>
  ({ dashed: 'border-t border-dashed', solid: 'border-t', double: 'border-t-2 border-double' }[s]);

const widthClass = (w: '80mm' | '58mm') => (w === '80mm' ? 'w-[320px]' : 'w-[232px]');

function Block({ block, logoUrl }: { block: ReceiptBlock; logoUrl: string | null }) {
  switch (block.type) {
    case 'text': {
      const cls = [
        sizeClass(block.size), alignClass(block.align), spacingClass(block.spacingAbove),
        block.bold && 'font-bold', block.italic && 'italic', block.underline && 'underline',
        block.inverse && 'bg-black text-white px-1',
      ].filter(Boolean).join(' ');
      return <div className={cls}>{block.content}</div>;
    }
    case 'boxed': {
      const cls = [
        sizeClass(block.size), alignClass(block.align), spacingClass(block.spacingAbove),
        block.bold && 'font-bold', dividerClass(block.borderStyle),
      ].filter(Boolean).join(' ');
      return <div className={`p-1 border ${cls}`}>{block.content}</div>;
    }
    case 'logo': {
      if (!logoUrl) return null;
      const max = ({ S: 'max-w-[80px]', M: 'max-w-[140px]', L: 'max-w-[200px]' }[block.maxWidth]);
      return (
        <div className={`${alignClass(block.align)} ${spacingClass(block.spacingAbove)}`}>
          <ShimmerImage src={logoUrl} alt="logo" width="auto" height="auto" objectFit="contain" imgClassName={`${max} mx-auto`} variant="light" />
        </div>
      );
    }
    case 'image': {
      const max = ({ S: 'max-w-[80px]', M: 'max-w-[140px]', L: 'max-w-[200px]' }[block.maxWidth]);
      return (
        <div className={`${alignClass(block.align)} ${spacingClass(block.spacingAbove)}`}>
          <ShimmerImage src={block.imageUrl} alt="" width="auto" height="auto" objectFit="contain" imgClassName={`${max} mx-auto`} variant="light" />
        </div>
      );
    }
    case 'qr':
      return <QrImg data={block.data} size={block.size} align={block.align} caption={block.caption} spacingAbove={block.spacingAbove} />;
    case 'divider':
      return <hr className={`${dividerClass(block.style)} border-black ${spacingClass(block.spacingAbove)}`} />;
    case 'spacer': {
      const h = ({ S: 'h-1', M: 'h-3', L: 'h-6' }[block.height]);
      return <div className={h} />;
    }
  }
}

function fmt(n: number) { return n.toFixed(2); }

export default function ReceiptPreview({ config, order, width }: Props) {
  return (
    <div data-testid="receipt-root" className={`${widthClass(width)} font-mono text-[12px] leading-[1.35] bg-[#fffaf2] text-black p-3 rounded shadow`}>
      {/* Header slot */}
      {config.slots.header.map((b) => <Block key={b.id} block={b} logoUrl={config.logoUrl} />)}

      {/* Business details — per-line styled overrides */}
      <div className="bg-stone-200/40 p-1">
        <BusinessLine line={config.businessDetailsOverride?.legalName} defaultContent={order.legalName} styleKey="legalName" />
        <BusinessLine line={config.businessDetailsOverride?.addressLine1} defaultContent={order.address} styleKey="addressLine1" />
        <BusinessLine line={config.businessDetailsOverride?.addressLine2} defaultContent={undefined} styleKey="addressLine2" />
        <BusinessLine line={config.businessDetailsOverride?.addressLine3} defaultContent={undefined} styleKey="addressLine3" />
        <BusinessLine line={config.businessDetailsOverride?.phone} defaultContent={order.phone} styleKey="phone" />
        <hr className="border-t border-black my-1" />
      </div>

      {/* Locked: order header + items */}
      <div className="bg-stone-200/40 p-1">
        <div>Order #{order.orderNumber} · {new Date(order.timestamp).toLocaleString()}</div>
        {order.cashier && <div>Cashier: {order.cashier}</div>}
        <hr className="border-t border-dashed border-black my-1" />
        {order.items.map((it, i) => (
          <div key={i}>
            <div>{it.qty} × {it.name} …………… {fmt(it.qty * it.price)}</div>
            {(it.discountSummary || []).map((d, j) => {
              const amt = Math.abs(Number(d.amount) || 0);
              if (amt === 0) return null;
              const prefix = d.source === 'promotion' ? 'Promo' : d.source === 'loyalty' ? 'Reward' : d.source === 'cash' ? 'Cash' : d.source === 'employee' ? 'Employee' : 'Manual';
              return (
                <div key={j} className="pl-3 text-stone-700">
                  {prefix}: {d.sourceName} …………… -{fmt(amt)}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Below items slot */}
      {config.slots.belowItems.map((b) => <Block key={b.id} block={b} logoUrl={config.logoUrl} />)}

      {/* Locked: totals */}
      <div className="bg-stone-200/40 p-1">
        <hr className="border-t border-dashed border-black my-1" />
        <div className="text-right">Subtotal: {fmt(order.subtotal)}</div>
        {order.discount != null && order.discount !== 0 && (
          <div className="text-right">Discount: -{fmt(Math.abs(order.discount))}</div>
        )}
        <div className="text-right">Tax: {fmt(order.tax)}</div>
        {order.fees?.map((f, i) => <div key={`f${i}`} className="text-right">{f.label}: {fmt(f.amount)}</div>)}
        {order.surcharges?.map((s, i) => <div key={`s${i}`} className="text-right">{s.label}: {fmt(s.amount)}</div>)}
        {order.tip != null && <div className="text-right">Tip: {fmt(order.tip)}</div>}
        <div className="text-right font-bold">TOTAL: {fmt(order.total)}</div>
        <div className="text-right text-[10px]">{order.payment}{order.change != null ? ` · Change ${fmt(order.change)}` : ''}</div>
      </div>

      {/* Locked: order barcode (default, always shown) */}
      <OrderBarcode value={order.orderNumber} />

      {/* Footer slot */}
      {config.slots.footer.map((b) => <Block key={b.id} block={b} logoUrl={config.logoUrl} />)}
    </div>
  );
}
